/* eslint-disable @typescript-eslint/no-empty-function */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { UsersAndTeams, SignalInstalled } from '@momentum/types';
import { SignalInstalledStatus } from '@momentum/types';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/Popover';
import { UserTeamPickerPopover } from '@/shared/components/user-team-select/UserTeamPickerPopover';
import { getEmployeeRoleDisplayName } from '@/shared/utils/role-utils';
import type { EmployeeInfo } from '../../../constants';
import type { AvatarItem } from './AvatarStack';
import { AvatarStack, getInitials } from './AvatarStack';
import { ReadOnlyList } from './ReadOnlyList';

interface VisibleToSelectProps {
  value: UsersAndTeams;
  installed?: SignalInstalled;
  isCreator: boolean;
  employeeMap: Map<string, EmployeeInfo>;
  teamMap: Map<string, string>;
  onChange?: (value: UsersAndTeams) => void;
}

export function VisibleToSelect({
  value,
  installed,
  isCreator,
  employeeMap,
  teamMap,
  onChange,
}: VisibleToSelectProps) {
  const { t } = useTranslation('knowledge');
  const { t: tCommon } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);

  const activeInstalledIds: UsersAndTeams = {
    users:
      installed?.users
        ?.filter((entry) => entry.status === SignalInstalledStatus.ACTIVE)
        .map((entry) => entry.userId) ?? [],
    teams:
      installed?.teams
        ?.filter((entry) => entry.status === SignalInstalledStatus.ACTIVE)
        .map((entry) => entry.teamId) ?? [],
  };

  const avatarItems: AvatarItem[] = [
    ...(value.teams ?? []).map((teamId) => ({
      type: 'team' as const,
      id: teamId,
      name: teamMap.get(teamId) ?? t('detailUnknownTeam'),
      initials: '',
    })),
    ...(value.users ?? []).map((userId) => {
      const emp = employeeMap.get(userId);
      return {
        type: 'user' as const,
        id: userId,
        name: emp?.fullName ?? userId,
        initials: emp ? getInitials(emp.fullName) : '??',
        imageUrl: emp?.imageUrl,
        role: emp?.role
          ? getEmployeeRoleDisplayName(emp.role, tCommon)
          : undefined,
      };
    }),
  ];

  const totalCount = (value.users?.length ?? 0) + (value.teams?.length ?? 0);

  if (totalCount === 0) {
    if (!isCreator) return null;
    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded border border-dashed border-border bg-transparent px-2 py-0.5 text-[12px] text-muted-foreground transition-colors hover:border-muted-foreground hover:text-foreground/70 cursor-pointer"
          >
            + {t('visibleToPlaceholder')}
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[240px] p-0 shadow-[0_8px_32px_rgba(0,0,0,0.10)]"
          align="start"
          sideOffset={4}
        >
          <UserTeamPickerPopover
            value={value}
            onChange={onChange ?? (() => {})}
            searchPlaceholder={t('pickerSearchPlaceholder')}
            teamsLabel={t('pickerTeamsGroup')}
            peopleLabel={t('pickerPeopleGroup')}
            noResultsLabel={t('pickerNoResults')}
            lockedIds={activeInstalledIds}
            lockedTooltip={t('lockedViewerTooltip')}
            onEscape={() => setIsOpen(false)}
          />
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="cursor-pointer rounded-md px-1 py-0.5 -mx-1 transition-colors hover:bg-black/[0.025]"
        >
          <AvatarStack items={avatarItems} totalCount={totalCount} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[240px] p-0 shadow-[0_8px_32px_rgba(0,0,0,0.10)]"
        align="start"
        sideOffset={4}
      >
        {isCreator ? (
          <UserTeamPickerPopover
            value={value}
            onChange={onChange ?? (() => {})}
            searchPlaceholder={t('pickerSearchPlaceholder')}
            teamsLabel={t('pickerTeamsGroup')}
            peopleLabel={t('pickerPeopleGroup')}
            noResultsLabel={t('pickerNoResults')}
            lockedIds={activeInstalledIds}
            lockedTooltip={t('lockedViewerTooltip')}
            onEscape={() => setIsOpen(false)}
          />
        ) : (
          <ReadOnlyList
            value={value}
            employeeMap={employeeMap}
            teamMap={teamMap}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
